import { InfoOutlined, EditOutlined } from '@mui/icons-material';
import { Button, CardContainer, Dropdown } from 'finara-components';
import type { DropdownOption } from 'finara-components';
import { useState } from 'react';
import toast from 'react-hot-toast';

import { Input } from '@/components/common/Input';
import {
  usePayrollConfig,
  usePayrollConfigStatus,
  useUpdatePayrollConfig,
} from '@/hooks/queries/useOrganizationConfig';

export const PayrollConfigCard: React.FC = () => {
  const { data: config, isLoading } = usePayrollConfig();
  const { data: status } = usePayrollConfigStatus();
  const updateConfig = useUpdatePayrollConfig();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    payrollFrequency: 'biweekly' as 'biweekly' | 'monthly',
    payrollDayOfMonth1: 15,
    payrollDayOfMonth2: null as number | null,
    payrollAutoDispersion: false,
    payrollDispersionTime: '09:00:00',
  });

  const frequencyOptions: DropdownOption[] = [
    { value: 'biweekly', label: 'Quincenal' },
    { value: 'monthly', label: 'Mensual' },
  ];

  const handleEdit = () => {
    if (config) {
      setFormData({
        payrollFrequency: config.payrollFrequency,
        payrollDayOfMonth1: config.payrollDayOfMonth1,
        payrollDayOfMonth2: config.payrollDayOfMonth2,
        payrollAutoDispersion: config.payrollAutoDispersion,
        payrollDispersionTime: config.payrollDispersionTime,
      });
    }
    setIsEditing(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.payrollDayOfMonth1 < 1 || formData.payrollDayOfMonth1 > 31) {
      toast.error('El día debe estar entre 1 y 31');
      return;
    }

    if (
      formData.payrollDayOfMonth2 !== null &&
      (formData.payrollDayOfMonth2 < 1 || formData.payrollDayOfMonth2 > 31)
    ) {
      toast.error('El segundo día debe estar entre 1 y 31');
      return;
    }

    try {
      await updateConfig.mutateAsync(formData);
      toast.success('Cambio enviado a revisión. Un super admin debe aprobarlo antes de aplicarse.');
      setIsEditing(false);
    } catch (_error) {
      toast.error('Error al actualizar la configuración');
    }
  };

  if (isLoading) {
    return (
      <CardContainer>
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-white/10 rounded w-1/3"></div>
            <div className="h-4 bg-white/10 rounded w-2/3"></div>
          </div>
        </div>
      </CardContainer>
    );
  }

  return (
    <CardContainer>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-brand-white">Configuración de Nómina</h3>
            <p className="text-sm text-muted mt-1">
              Define la frecuencia y días de pago de nómina
            </p>
          </div>
          {!isEditing && (
            <Button variant="white" size="small" onClick={handleEdit}>
              <EditOutlined fontSize="small" className="mr-1" />
              Editar
            </Button>
          )}
        </div>

        {status?.usingDefaults && (
          <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <div className="flex items-start gap-2">
              <InfoOutlined className="text-brand-blue mt-0.5" fontSize="small" />
              <div className="flex-1">
                <p className="text-sm font-medium text-brand-white">Usando valores predeterminados</p>
                <p className="text-xs text-muted mt-1">{status.warnings.join(' ')}</p>
              </div>
            </div>
          </div>
        )}

        {isEditing ? (
          <form onSubmit={e => void handleSubmit(e)} className="space-y-4">
            <div>
              <label
                htmlFor="payrollFrequency"
                className="block text-sm font-medium text-brand-white mb-1"
              >
                Frecuencia de Nómina
              </label>
              <Dropdown
                id="payrollFrequency"
                value={formData.payrollFrequency}
                onChange={value =>
                  setFormData(prev => ({
                    ...prev,
                    payrollFrequency: String(value) as 'biweekly' | 'monthly',
                  }))
                }
                options={frequencyOptions}
                disabled={updateConfig.isPending}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input
                  id="payrollDayOfMonth1"
                  label="Primer Día de Pago"
                  type="number"
                  value={formData.payrollDayOfMonth1}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      payrollDayOfMonth1: Number(e.target.value),
                    }))
                  }
                  min="1"
                  max="31"
                  disabled={updateConfig.isPending}
                />
              </div>

              <div>
                <Input
                  id="payrollDayOfMonth2"
                  label="Segundo Día (opcional)"
                  type="number"
                  value={formData.payrollDayOfMonth2 || ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      payrollDayOfMonth2: e.target.value ? Number(e.target.value) : null,
                    }))
                  }
                  min="1"
                  max="31"
                  placeholder="Último día del mes"
                  disabled={updateConfig.isPending}
                />
              </div>
            </div>

            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <div className="flex items-center gap-2">
                <InfoOutlined className="text-brand-blue mt-0.5" fontSize="small" />
                <p className="text-xs text-muted">
                  Si el segundo día es mayor al último día del mes, el sistema usará automáticamente
                  el último día del mes.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                id="payrollAutoDispersion"
                type="checkbox"
                checked={formData.payrollAutoDispersion}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    payrollAutoDispersion: e.target.checked,
                  }))
                }
                className="rounded border-border"
                disabled={updateConfig.isPending}
              />
              <label htmlFor="payrollAutoDispersion" className="text-sm text-brand-white">
                Dispersión automática de nómina
              </label>
            </div>

            {formData.payrollAutoDispersion && (
              <div>
                <Input
                  id="payrollDispersionTime"
                  label="Hora de Dispersión"
                  type="time"
                  value={formData.payrollDispersionTime}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      payrollDispersionTime: e.target.value + ':00',
                    }))
                  }
                  disabled={updateConfig.isPending}
                />
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button type="submit" variant="black" size="small" disabled={updateConfig.isPending}>
                {updateConfig.isPending ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
              <Button
                type="button"
                variant="white"
                size="small"
                onClick={() => setIsEditing(false)}
                disabled={updateConfig.isPending}
              >
                Cancelar
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted">Frecuencia</p>
                <p className="text-lg font-semibold text-brand-white capitalize">
                  {config?.payrollFrequency === 'biweekly' ? 'Quincenal' : 'Mensual'}
                </p>
              </div>

              <div>
                <p className="text-sm text-muted">Días de Pago</p>
                <p className="text-lg font-semibold text-brand-white">
                  {config?.payrollDayOfMonth1}
                  {config?.payrollDayOfMonth2 && ` y ${config.payrollDayOfMonth2}`}
                  {!config?.payrollDayOfMonth2 && ' y último día'}
                </p>
              </div>

              <div>
                <p className="text-sm text-muted">Dispersión Automática</p>
                <p className="text-lg font-semibold text-brand-white">
                  {config?.payrollAutoDispersion ? 'Activada' : 'Desactivada'}
                </p>
              </div>

              {config?.payrollAutoDispersion && (
                <div>
                  <p className="text-sm text-muted">Hora de Dispersión</p>
                  <p className="text-lg font-semibold text-brand-white">
                    {config.payrollDispersionTime.slice(0, 5)}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </CardContainer>
  );
};
